import React, { useEffect, useRef, useState } from 'react';
import { useMusic } from './MusicPlayerProvider';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Music2, ChevronDown } from 'lucide-react';

const MusicBar = () => {
  const { items, current, currentItem, playing, volume, muted, next, prev, togglePlay, toggleMute, setVol, selectIndex, setMuted } = useMusic();
  const [expanded, setExpanded] = useState(false);
  const [showVolume, setShowVolume] = useState(false);
  const [volumeEntered, setVolumeEntered] = useState(false);
  const [volumePlacement, setVolumePlacement] = useState('top-left');
  const idleTimer = useRef(null);
  const hasStartedRef = useRef(false);
  const rootRef = useRef(null);
  const volumeButtonRef = useRef(null);
  const volumePanelRef = useRef(null);

  const handleVolumeChange = (value) => {
    const nextVolume = value[0] ?? 0;
    setVol(nextVolume);
    setMuted(nextVolume === 0);
  };

  const startIdleTimer = () => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (!expanded) return;
    idleTimer.current = setTimeout(() => {
      setExpanded(false);
      setShowVolume(false);
    }, 5000);
  };
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const onInteract = () => startIdleTimer();
    ['mousemove', 'mousedown', 'touchstart', 'wheel', 'keydown'].forEach(ev => el.addEventListener(ev, onInteract, { passive: true }));
    startIdleTimer();
    return () => {
      ['mousemove', 'mousedown', 'touchstart', 'wheel', 'keydown'].forEach(ev => el.removeEventListener(ev, onInteract));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [expanded]);

  useEffect(() => {
    if (!showVolume) return undefined;

    setVolumeEntered(false);
    const frame = window.requestAnimationFrame(() => setVolumeEntered(true));

    const updateVolumePlacement = () => {
      const buttonRect = volumeButtonRef.current?.getBoundingClientRect();
      if (!buttonRect) return;

      const panelWidth = 88;
      const panelHeight = 212;
      const spaceAbove = buttonRect.top;
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const spaceLeft = buttonRect.left;
      const spaceRight = window.innerWidth - buttonRect.right;

      const vertical = spaceAbove >= panelHeight || spaceAbove >= spaceBelow ? 'top' : 'bottom';

      let horizontal = 'center';
      if (spaceLeft < panelWidth / 2) horizontal = 'left';
      else if (spaceRight < panelWidth / 2) horizontal = 'right';

      setVolumePlacement(`${vertical}-${horizontal}`);
    };

    const handlePointerDown = (event) => {
      const target = event.target;
      if (
        volumePanelRef.current?.contains(target) ||
        volumeButtonRef.current?.contains(target)
      ) {
        return;
      }
      setShowVolume(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowVolume(false);
      }
    };

    updateVolumePlacement();
    window.addEventListener('resize', updateVolumePlacement);
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.cancelAnimationFrame(frame);
      setVolumeEntered(false);
      window.removeEventListener('resize', updateVolumePlacement);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showVolume]);

  if (!currentItem) return null;

  const volumePanelPositionClass = {
    'top-left': 'bottom-full left-0 mb-3',
    'top-center': 'bottom-full left-1/2 mb-3 -translate-x-1/2',
    'top-right': 'bottom-full right-0 mb-3',
    'bottom-left': 'top-full left-0 mt-3',
    'bottom-center': 'top-full left-1/2 mt-3 -translate-x-1/2',
    'bottom-right': 'top-full right-0 mt-3',
  }[volumePlacement];

  const volumePanelOriginClass = volumePlacement.startsWith('top') ? 'origin-bottom' : 'origin-top';

  const handleOpenPlayer = () => {
    setExpanded(true);

    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      setMuted(false);

      if (!playing) {
        togglePlay();
      }
    }
  };

  if (!expanded) {
    return (
      <button
        ref={rootRef}
        className="group inline-flex fixed right-4 bottom-4 z-40 justify-center items-center w-14 h-14 text-white rounded-full
        border border-white/12 shadow-[0_14px_34px_rgba(0,0,0,0.34)]
        bg-black/88 md:bg-black/78
        transition-[transform,background-color,border-color,box-shadow] duration-200 hover:scale-[1.03] hover:bg-black/88 hover:border-white/20 hover:shadow-[0_20px_52px_rgba(0,0,0,0.5)]"
        style={{ bottom: 'max(1rem, calc(var(--app-safe-bottom) + 0.75rem))' }}
        title="Ouvrir le lecteur"
        onClick={handleOpenPlayer}
      >
        <span className="relative z-10 inline-flex justify-center items-center w-10 h-10 rounded-full border border-white/10 bg-white/[0.06] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
          {muted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
        </span>
      </button>
    );
  }

  return (
    <div
      ref={rootRef}
      className="fixed right-3 bottom-3 left-3 z-40 overflow-visible md:right-4 md:bottom-4 md:left-auto md:w-[460px]
      rounded-[1.5rem] border border-white/10
      shadow-[0_18px_44px_rgba(0,0,0,0.36)] md:shadow-[0_24px_65px_rgba(0,0,0,0.48)]
      bg-[linear-gradient(160deg,rgba(6,6,6,0.96)_0%,rgba(18,18,18,0.94)_55%,rgba(8,8,8,0.98)_100%)]
      transition-[opacity,transform] duration-180 ease-out transform-gpu opacity-100 md:backdrop-blur-xl"
      style={{
        bottom: 'max(0.75rem, calc(var(--app-safe-bottom) + 0.5rem))',
        willChange: 'transform, opacity'
      }}
    >
      <div className="flex flex-col gap-3 p-3.5 sm:flex-row sm:items-center sm:p-4">
        <div className="flex gap-3 items-center min-w-0 flex-1">
          <div className="relative shrink-0">
          <button
            ref={volumeButtonRef}
            aria-expanded={showVolume}
            className="inline-flex justify-center items-center w-11 h-11 text-white rounded-full
            border border-white/10 bg-white/[0.06]
            shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]
            transition-[background-color,border-color,transform] duration-200 hover:bg-white/[0.1] hover:border-white/16"
            title={muted ? 'Activer le son' : 'Couper le son'}
            onClick={() => setShowVolume(v => !v)}
          >
            {muted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          {showVolume ? (
            <div
              ref={volumePanelRef}
              className={`absolute ${volumePanelPositionClass} ${volumePanelOriginClass} z-20 flex flex-col items-center gap-3 rounded-[1.25rem] px-3 py-3
              border border-white/10
              bg-[linear-gradient(180deg,rgba(10,10,10,0.96)_0%,rgba(18,18,18,0.94)_100%)]
              shadow-[0_18px_40px_rgba(0,0,0,0.45)]
              transition-[opacity,transform] duration-150 ease-out md:backdrop-blur-xl ${
                volumeEntered
                  ? 'opacity-100 scale-100 translate-y-0'
                  : volumePlacement.startsWith('top')
                    ? 'opacity-0 scale-95 translate-y-1'
                    : 'opacity-0 scale-95 -translate-y-1'
              }`}
            >
              <span className="text-[10px] font-medium tracking-[0.2em] text-white/45 uppercase">Volume</span>
              <div className="flex justify-center h-36">
                <Slider
                  orientation="vertical"
                  className="h-full"
                  value={[muted ? 0 : volume]}
                  min={0}
                  max={100}
                  step={1}
                  onValueChange={handleVolumeChange}
                />
              </div>
              <span className="text-xs font-semibold text-white">{muted ? 0 : volume}%</span>
              <Button
                size="icon"
                variant="ghost"
                className="w-9 h-9 text-white/80 rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/[0.1] hover:text-white"
                onClick={toggleMute}
                title={muted ? 'Activer le son' : 'Couper le son'}
              >
                  {muted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </Button>
            </div>
          ) : null}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex gap-2 items-center mb-1 text-[10px] font-medium tracking-[0.22em] text-white/42 uppercase">
              <Music2 size={14} className="text-white/32 shrink-0" />
              <span className="truncate">{playing ? 'Now playing' : 'Ready to play'}</span>
            </div>
            <div className="text-sm font-semibold tracking-[0.01em] text-white truncate">
              {currentItem.title || currentItem.url}
            </div>
          </div>
        </div>
        <div className="flex gap-1.5 items-center justify-between sm:justify-start">
          <Button size="icon" variant="ghost" className="w-10 h-10 text-white/78 rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/[0.1] hover:text-white" onClick={prev} title="Précédent"><SkipBack size={18} /></Button>
          <Button
            size="icon"
            variant="ghost"
            className="w-11 h-11 rounded-full border border-white/14 bg-white text-black shadow-[0_12px_28px_rgba(0,0,0,0.3)] hover:bg-white/90 hover:text-black"
            onClick={togglePlay}
            title={playing ? 'Pause' : 'Lecture'}
          >
            {playing ? <Pause size={18} /> : <Play size={18} />}
          </Button>
          <Button size="icon" variant="ghost" className="w-10 h-10 text-white/78 rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/[0.1] hover:text-white" onClick={next} title="Suivant"><SkipForward size={18} /></Button>
          <Button size="icon" variant="ghost" className="w-10 h-10 text-white/72 rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/[0.1] hover:text-white" title="Fermer" onClick={() => { setExpanded(false); setShowVolume(false); }}>
            <ChevronDown size={18} />
          </Button>
        </div>
      </div>
      {items.length > 1 ? (
        <div className="px-3.5 pb-3.5 sm:px-4 sm:pb-4 transition-opacity duration-200">
          <div className="flex overflow-x-auto gap-1.5 no-scrollbar">
            {items.map((it, i) => (
              <button
                key={it.id}
                onClick={() => selectIndex(i)}
                className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap border transition-colors ${
                  i === current
                    ? 'border-white/18 bg-white/[0.12] text-white'
                    : 'border-white/10 bg-white/[0.04] text-white/62 hover:bg-white/[0.08] hover:text-white/82'
                }`}
                title={it.title || it.url}
              >
                {it.title || `Morceau ${i + 1}`}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default MusicBar;
